/**
 * <TickerSearchInput> — 재사용 ticker 검색 input + autocomplete.
 *
 * BITE / ENTRY landing / 기타 ticker 입력이 필요한 곳 어디서나 plug-in.
 * 디자인 (background/border/color)은 부모가 className 또는 wrapper 로 제어.
 *
 * 동작:
 *   - 입력 → 짧은 입력 대기 → 최신 요청의 결과 표시
 *   - 화살표 키로 navigate / Enter 로 선택
 *   - submit → onSelect(normalizedSymbol) 콜백 (router.push 는 caller 가 결정)
 */
"use client";

import { useState, useEffect, useId, useRef } from "react";
import { ArrowUp, LoaderCircle, Search } from "lucide-react";
import { api } from "@/lib/api";
import { stripName } from "@/lib/strip-name";

import {createTickerAutocomplete,type AutocompleteState} from '@/lib/ticker-autocomplete';

interface Props {
  /** 호출자가 선택된 ticker 받음 (router.push 등 처리) */
  onSelect: (symbol: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** wrapper / pill 의 클래스 — caller 가 BITE/ENTRY 테마 적용 */
  className?: string;
  /** 우측 → 버튼 라벨 (접근성용) */
  submitLabel?: string;
  /** 자동완성 선택 없이 제출한 종목명도 ticker 로 해석 */
  resolveCompanyNames?: boolean;
}

function normalizeTicker(raw: string): string {
  const cleaned = raw.trim().replace(/\s+/g, "").replace(/^\$/, "");
  const kr = cleaned.match(/^(\d{6})(?:\.(KS|KQ))?$/i);
  if (kr) return kr[1];
  const us = cleaned.match(/^([A-Za-z]{1,5})(?:\.[A-Za-z]+)?$/);
  if (us) return us[1].toUpperCase();
  return cleaned.toUpperCase();
}

export function TickerSearchInput({
  onSelect,
  placeholder = "예: 테슬라, 애플, 엔비디아",
  autoFocus = true,
  className = "",
  submitLabel = "검색",
  resolveCompanyNames = false,
}: Props) {
  const [query, setQuery] = useState("");
  const [searchState,setSearchState]=useState<AutocompleteState>({query:'',items:[],loading:false,error:''});
  const suggestions=searchState.items;
  const [autocomplete]=useState(()=>createTickerAutocomplete(async(q,signal)=>{
    const data=await api<{items?:unknown[]}>(`/api/tickers?q=${encodeURIComponent(q)}&mode=ac`,{signal});
    return data.items;
  },setSearchState));
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState("");
  const statusId = useId();
  const submission=useRef(0);
  useEffect(()=>()=>{submission.current++;autocomplete.dispose();},[autocomplete]);

  const handleInputChange = (val: string) => {
    submission.current++;setIsResolving(false);
    setQuery(val);setError('');setFocusedIndex(-1);
    setShowSuggestions(Boolean(val.trim()));autocomplete.search(val);
  };

  const submit = async (raw?: string, selectedSymbol = false) => {
    const value = (raw || query).trim();
    if (!value || isResolving) return;
    const submissionId=++submission.current;
    setShowSuggestions(false);
    setError("");

    if (!resolveCompanyNames) {
      onSelect(normalizeTicker(value));
      return;
    }

    const normalized = normalizeTicker(value);
    const directTicker = /^\$?[A-Z]{1,5}(?:\.[A-Z]+)?$/i.test(value) || /^\d{6}(?:\.(?:KS|KQ))?$/i.test(value);
    setIsResolving(true);
    try {
      const selectVerified=async(symbol:string)=>{
        const resolved=await api<{ticker?:string}>(`/api/tickers/resolve?q=${encodeURIComponent(normalizeTicker(symbol))}`);
        if(submissionId===submission.current&&resolved.ticker)onSelect(normalizeTicker(resolved.ticker));
      };
      if(directTicker||selectedSymbol){await selectVerified(normalized);return;}
      const candidates = await autocomplete.resolve(value);
      if(submissionId!==submission.current)return;

      const matched = candidates.find((item) => item.symbol.toUpperCase() === normalized) || candidates[0];
      if (matched) {
        await selectVerified(matched.symbol);
        return;
      }

      try {
        const resolved = await api<{ ticker?: unknown }>(`/api/tickers/resolve?q=${encodeURIComponent(value)}`);
        if(submissionId!==submission.current)return;
        if (typeof resolved?.ticker === "string" && resolved.ticker.trim()) {
          await selectVerified(resolved.ticker);
          return;
        }
      } catch(error) {
        if(submissionId!==submission.current)return;
        setError(error instanceof Error?error.message:"종목을 찾지 못했습니다. 티커나 종목명을 다시 확인하세요.");
        return;
      }
      setError("종목을 찾지 못했습니다. 티커나 종목명을 다시 확인하세요.");
    } catch(error) {
      if(submissionId===submission.current)setError(error instanceof Error?error.message:"종목 정보를 불러오지 못했습니다. 다시 시도해주세요.");
    } finally {
      if(submissionId===submission.current)setIsResolving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((p) => Math.min(p + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((p) => Math.max(p - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (!searchState.loading && focusedIndex >= 0 && suggestions[focusedIndex]) {
        void submit(suggestions[focusedIndex].symbol, true);
      } else {
        void submit();
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };



  return (
    <div className={`tsi-wrap ${className}`}>
      <form
        className="tsi-form"
        onSubmit={(e) => { e.preventDefault(); void submit(); }}
        aria-busy={isResolving || searchState.loading}
      >
        <Search className="tsi-icon" aria-hidden="true" />
        <input
          type="text"
          className="tsi-input"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder={placeholder}
          aria-describedby={error || isResolving || searchState.loading || searchState.error ? statusId : undefined}
          aria-invalid={error ? true : undefined}
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
          spellCheck={false}
          autoFocus={autoFocus}
        />
        <button
          type="submit"
          className="tsi-go"
          aria-label={submitLabel}
          disabled={isResolving || !query.trim()}
        >
          {isResolving ? <LoaderCircle className="tsi-go-spinner" aria-hidden="true" /> : <ArrowUp aria-hidden="true" />}
        </button>
      </form>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="tsi-suggestions" role="listbox" aria-busy={searchState.loading} aria-label="검색한 종목">
          {suggestions.map((item, i) => (
            <li
              key={item.symbol}
              role="option"
              aria-selected={i === focusedIndex}
              className={`tsi-suggestion ${i === focusedIndex ? "is-focused" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();           // input blur 방지
                void submit(item.symbol, true);
              }}
            >
              <span className="tsi-suggestion-symbol">{item.symbol}</span>
              {item.name && <span className="tsi-suggestion-name">{stripName(item.name)}</span>}
            </li>
          ))}
        </ul>
      )}
      {isResolving && <p className="tsi-status" id={statusId} role="status">종목을 확인하고 있습니다.</p>}
      {!isResolving && <>{searchState.loading
        ? <p className="tsi-status" id={statusId} role="status">종목을 찾고 있습니다.</p>
        : (error || searchState.error) && <p className="tsi-status" id={statusId} role="status">{error || searchState.error}</p>}</>}
    </div>
  );
}
